/**
 * useNetworkDetection Hook
 *
 * Wraps the Network Information API to detect connection type (WiFi/cellular)
 * and connection quality. Reacts to network changes in real-time.
 *
 * @example
 * const { isCellular, connectionType, isSlowConnection } = useNetworkDetection()
 *
 * if (isCellular && fileSize > 3MB) {
 *   showDataWarning()
 * }
 */

import { useState, useEffect } from 'react'
import {
  getConnectionQuality,
  isCellularConnection,
  supportsNetworkInformation
} from '../utils/networkHelpers'

export type ConnectionType = 'wifi' | 'cellular' | 'unknown'
export type ConnectionQuality = 'slow' | 'normal' | 'fast'

export interface UseNetworkDetectionReturn {
  /**
   * Connection type: wifi, cellular, or unknown
   */
  connectionType: ConnectionType

  /**
   * Effective connection type from the API ('4g', '3g', '2g', 'slow-2g')
   */
  effectiveType: string

  /**
   * Download speed in Mbps (from Network Information API)
   */
  downlink: number

  /**
   * Round-trip time in milliseconds
   */
  rtt: number

  /**
   * Whether browser data saver mode is enabled
   */
  saveData: boolean

  /**
   * Whether the connection is considered slow
   */
  isSlowConnection: boolean

  /**
   * Whether the connection is cellular (4G, 3G, 2G)
   */
  isCellular: boolean

  /**
   * Connection quality classification (slow, normal, fast)
   */
  connectionQuality: ConnectionQuality

  /**
   * Whether the Network Information API is supported
   */
  supported: boolean
}

/**
 * Gets the NetworkInformation object with vendor prefixes
 */
function getConnection(): any {
  if (typeof navigator === 'undefined') {
    return null
  }

  return (
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection ||
    null
  )
}

/**
 * Hook for Network Information API
 *
 * Detects WiFi vs cellular data and connection quality.
 * Automatically updates when network conditions change.
 *
 * Note: Network Information API is not supported in all browsers
 * (notably Firefox and Safari iOS). Falls back to 'unknown' values.
 *
 * @returns Network detection state
 */
export function useNetworkDetection(): UseNetworkDetectionReturn {
  const supported = supportsNetworkInformation()

  const [networkState, setNetworkState] = useState<Omit<UseNetworkDetectionReturn, 'supported'>>(() => {
    if (!supported) {
      return {
        connectionType: 'unknown',
        effectiveType: 'unknown',
        downlink: 0,
        rtt: 0,
        saveData: false,
        isSlowConnection: false,
        isCellular: false,
        connectionQuality: 'normal'
      }
    }

    const connection = getConnection()
    const effectiveType = connection?.effectiveType || 'unknown'
    const downlink = connection?.downlink || 0
    const rtt = connection?.rtt || 0
    const saveData = connection?.saveData || false

    // Determine connection type
    let connectionType: ConnectionType = 'unknown'
    if (connection?.type) {
      // type property gives 'wifi', 'cellular', 'ethernet', etc.
      if (connection.type === 'wifi' || connection.type === 'ethernet') {
        connectionType = 'wifi'
      } else if (connection.type === 'cellular') {
        connectionType = 'cellular'
      }
    } else if (effectiveType !== 'unknown') {
      // Fall back to effectiveType analysis if type not available
      connectionType = isCellularConnection(effectiveType) ? 'cellular' : 'wifi'
    }

    const isCellular = connectionType === 'cellular'
    const connectionQuality = getConnectionQuality(effectiveType, downlink)
    const isSlowConnection = connectionQuality === 'slow'

    return {
      connectionType,
      effectiveType,
      downlink,
      rtt,
      saveData,
      isSlowConnection,
      isCellular,
      connectionQuality
    }
  })

  useEffect(() => {
    if (!supported) {
      return
    }

    const connection = getConnection()
    if (!connection) {
      return
    }

    /**
     * Handle network change events
     */
    const handleChange = () => {
      const effectiveType = connection.effectiveType || 'unknown'
      const downlink = connection.downlink || 0
      const rtt = connection.rtt || 0
      const saveData = connection.saveData || false

      // Determine connection type
      let connectionType: ConnectionType = 'unknown'
      if (connection.type) {
        if (connection.type === 'wifi' || connection.type === 'ethernet') {
          connectionType = 'wifi'
        } else if (connection.type === 'cellular') {
          connectionType = 'cellular'
        }
      } else if (effectiveType !== 'unknown') {
        connectionType = isCellularConnection(effectiveType) ? 'cellular' : 'wifi'
      }

      const isCellular = connectionType === 'cellular'
      const connectionQuality = getConnectionQuality(effectiveType, downlink)
      const isSlowConnection = connectionQuality === 'slow'

      setNetworkState({
        connectionType,
        effectiveType,
        downlink,
        rtt,
        saveData,
        isSlowConnection,
        isCellular,
        connectionQuality
      })
    }

    // Listen for network changes
    connection.addEventListener('change', handleChange)

    return () => {
      connection.removeEventListener('change', handleChange)
    }
  }, [supported])

  return {
    ...networkState,
    supported
  }
}
